/* eslint-disable no-unused-vars */
import { isValidISODateString } from "@/lib/businessRules/settlementDateRules";
import { ISearchResult } from "@/lib/referenceDataSearchHelper";
import { IReferenceDataOption, IReferenceDataOptions } from "../ticketFields/ReferenceDataSelect";

// LQC-2493 - the parts of a ticket that do not care which instrument it writes: the
// required-field check, the send button's labels and the sent-confirmation timings.
// Bond had all of this inline; Future needs the same behaviour, so it lives here once
// rather than being copied per instrument.

export interface ITicketField<T> {
  key: keyof T;
  label: string;
  // Numbers must be present AND greater than zero - 0 nominal is not a filled field.
  isNumeric?: boolean;
  // Dates must be a real yyyy-MM-dd the API can parse. A native date input can yield a
  // five digit year, which is truthy but unsendable (LQC-2677).
  isDate?: boolean;
}

// Props for an instrument's CoreFields / DetailFields. Hosts that never flag rows (a
// Trade Creation draft) can omit highlightedKeys.
export interface ITicketFieldsProps {
  ticketId: string;
  highlightedKeys?: Set<string>;
  // Trade Creation's Security Details repeats the security (spec F6); Leg Creation
  // already shows it editable above, so it leaves this off.
  showSecurityIdentity?: boolean;
}

export const TICKET_EMPTY_VALUE = "-";

// LQC-2941 - the External ID travels as tradeDetails.transactionNumber, and only when set.
export function returnTransactionNumberDetail(
  transactionNumber: string | undefined,
): { transactionNumber?: string } {
  const trimmed = (transactionNumber ?? "").trim();
  return trimmed ? { transactionNumber: trimmed } : {};
}

export function returnMissingTicketEntries<T>(
  ticket: T,
  requiredFields: ITicketField<T>[],
): ITicketField<T>[] {
  return requiredFields.filter(({ key, isNumeric, isDate }) => {
    const value = ticket[key];
    if (isNumeric) return !(typeof value === "number" && value > 0);
    if (isDate) return !isValidISODateString(value as string);
    return !value;
  });
}

// The same check as plain { key, label } issues, so field and allocation issues share
// one list (LQC-2941).
export function returnTicketFieldIssues<T>(
  ticket: T,
  requiredFields: ITicketField<T>[],
): { key: string; label: string }[] {
  return returnMissingTicketEntries(ticket, requiredFields).map(({ key, label }) => ({
    key: String(key),
    label,
  }));
}

// The keys of every required field, for the "*" a ticket shows against a required row.
// Required is a property of the field list, not of the current value, so the marker is
// static - it does not appear and disappear as the user fills the ticket in.
export function returnRequiredTicketKeys<T>(
  requiredFields: ITicketField<T>[],
): Set<string> {
  return new Set(requiredFields.map(({ key }) => String(key)));
}

// Labels only - what the Send button reports and the toast repeats. Keys come from
// returnMissingTicketEntries, which the ticket uses to highlight the rows themselves.
export function returnMissingTicketFields<T>(
  ticket: T,
  requiredFields: ITicketField<T>[],
): string[] {
  return returnMissingTicketEntries(ticket, requiredFields).map(
    ({ label }) => label,
  );
}

// How long the "Sent ..." line stays, and how long the button holds its own confirmation.
// The button reverts first so it is usable again while the line still records the send.
export const SENT_CONFIRMATION_MS = 8000;
export const SENT_BUTTON_CONFIRMATION_MS = 2000;

export function returnSendButtonLabel(
  isSending: boolean,
  isConfirming: boolean,
): string {
  if (isSending) return "Sending ...";
  if (isConfirming) return "Sent ✓";
  return "Send to Trade Creation";
}

// 24 hour HH:mm:ss. Local to the widget because the app's only other time formatter
// builds export filenames.
export function returnSentAtTimeLabel(sentAt: number): string {
  const date = new Date(sentAt);
  return [date.getHours(), date.getMinutes(), date.getSeconds()]
    .map((part) => String(part).padStart(2, "0"))
    .join(":");
}

// Measured from sentAt rather than run as a fixed timer, so a widget remounted by a tab
// switch does not restart the countdown on a confirmation that has already expired.
export function returnSentConfirmationRemainingMs(
  sentAt: number,
  now: number,
): number {
  return Math.max(0, SENT_CONFIRMATION_MS - (now - sentAt));
}

// Both reference-data endpoints publish the same price block, so both tickets derive an
// indicative price the same way.
export interface IPricedSecurity {
  midPrice: number | null;
  bidPrice: number | null;
  askPrice: number | null;
}

// Mid where the payload has one, otherwise the bid/ask midpoint. Null is normal - plenty
// of rows come back with no published price.
export function returnIndicativePrice(
  security: IPricedSecurity | undefined,
): number | null {
  if (!security) return null;
  if (typeof security.midPrice === "number") return security.midPrice;
  if (
    typeof security.bidPrice === "number" &&
    typeof security.askPrice === "number"
  ) {
    return (security.bidPrice + security.askPrice) / 2;
  }
  return null;
}

// Map a search result to dropdown options, carrying the pre-cap match count through so
// the dropdown can say how much it is not showing.
export function returnReferenceDataOptions<T>(
  result: ISearchResult<T>,
  toOption: (item: T) => IReferenceDataOption,
): IReferenceDataOptions {
  return {
    options: result.items.map(toOption),
    totalMatches: result.totalMatches,
  };
}
